import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";

import { AiAssistantSheet } from "@/components/layout/AiAssistantSheet";
import { AppSidebar } from "@/components/layout/AppSidebar";
import { BottomNav } from "@/components/layout/BottomNav";
import { InstallBanner } from "@/components/layout/InstallBanner";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { TrialExpiryDialog } from "@/components/layout/TrialExpiryDialog";
import { Button } from "@/components/ui/button";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { useDemo } from "@/contexts/DemoContext";
import { useAutoReady } from "@/hooks/useAutoReady";
import { useBusinessSettings } from "@/hooks/useBusinessSettings";
import { useIsMobile } from "@/hooks/use-mobile";

const ROUTE_TITLES: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/bookings": "Bookings",
  "/fleet": "Fleet",
  "/customers": "Customers",
  "/financials": "Financials",
  "/maintenance": "Maintenance",
  "/alerts": "Alerts",
  "/settings": "Settings",
  "/map": "Map",
  "/availability-table": "Availability",
};

export function AppLayout() {
  const isMobile = useIsMobile();
  const { isDemo } = useDemo();
  const location = useLocation();
  const pageTitle = ROUTE_TITLES[location.pathname] ?? null;

  useAutoReady();

  const [aiOpen, setAiOpen] = useState(false);
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);

  const { business } = useBusinessSettings();

  useEffect(() => {
    if (isDemo || !business) return;

    const trialEndsAt = business.trial_ends_at ? new Date(business.trial_ends_at) : null;
    const trialExpired = !!trialEndsAt && trialEndsAt < new Date();
    const noSubscription = !(business as { paddle_subscription_id?: string | null }).paddle_subscription_id;

    if (trialExpired && noSubscription) {
      const dismissed = sessionStorage.getItem("trial_dialog_dismissed");
      if (!dismissed) setTrialDialogOpen(true);
    }
  }, [business, isDemo]);

  return (
    <SidebarProvider>
      <div className="flex min-h-[100dvh] w-full">
        {!isMobile && <AppSidebar />}

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-card/80 px-4 backdrop-blur-sm">
            {!isMobile && <SidebarTrigger />}
            {isMobile && pageTitle && (
              <span className="font-display text-base font-semibold text-foreground">{pageTitle}</span>
            )}
            <div className="flex-1" />
            {isDemo && (
              <span className="rounded-full border border-brand/20 bg-brand/10 px-2.5 py-1 text-[11px] font-medium text-brand sm:text-xs">
                Demo workspace
              </span>
            )}
            <Button
              data-tour="header-ai-assistant"
              variant="ghost"
              size="icon"
              onClick={() => setAiOpen(true)}
              className="text-muted-foreground hover:text-primary"
            >
              <Sparkles className="h-5 w-5" />
            </Button>
            <NotificationBell />
          </header>

          <main className="flex-1 overflow-y-auto pb-24 md:pb-6"><Outlet /></main>
        </div>

        {isMobile && <BottomNav />}
        <InstallBanner />
        <TrialExpiryDialog
          open={trialDialogOpen}
          onOpenChange={(open) => {
            if (!open) sessionStorage.setItem("trial_dialog_dismissed", "1");
            setTrialDialogOpen(open);
          }}
        />
        {aiOpen && <AiAssistantSheet open onOpenChange={setAiOpen} />}
      </div>
    </SidebarProvider>
  );
}
